/**
 * US-style opt-out consent for advertising cookies.
 *
 * Model: ad tags are allowed by default (US opt-out standard), EXCEPT when the
 * visitor has opted out or their browser sends a Global Privacy Control signal.
 * Google Consent Mode v2 defaults are set in index.html before GTM loads;
 * this module updates them when the visitor makes a choice.
 */

export const CONSENT_STORAGE_KEY = "wg_ad_consent";
export const CONSENT_CHOICE_MADE_KEY = "wg_ad_consent_choice_made";
export const OPEN_PREFERENCES_EVENT = "wg:open-cookie-preferences";

export type ConsentValue = "granted" | "denied";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
  }
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

/** True when the browser sends a Global Privacy Control opt-out signal. */
export const hasGlobalPrivacyControl = (): boolean => {
  if (typeof navigator === "undefined") return false;
  return navigator.globalPrivacyControl === true;
};

/** Has the visitor explicitly accepted or rejected? */
export const hasMadeConsentChoice = (): boolean => {
  try {
    return localStorage.getItem(CONSENT_CHOICE_MADE_KEY) === "1";
  } catch {
    return false;
  }
};

/** Current effective consent for advertising cookies. */
export const getAdConsent = (): ConsentValue => {
  if (hasGlobalPrivacyControl()) return "denied";
  try {
    return localStorage.getItem(CONSENT_STORAGE_KEY) === "denied" ? "denied" : "granted";
  } catch {
    return "granted";
  }
};

/** Persist a choice and push it to Google Consent Mode + the dataLayer. */
export const setAdConsent = (value: ConsentValue) => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, value);
    localStorage.setItem(CONSENT_CHOICE_MADE_KEY, "1");
  } catch {
    /* storage unavailable — the in-memory push below still applies */
  }
  applyConsentToGoogle(value);
};

/** Push the consent state into Google Consent Mode v2 and the dataLayer. */
export const applyConsentToGoogle = (value: ConsentValue) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: value,
      ad_user_data: value,
      ad_personalization: value,
      analytics_storage: value,
    });
  }
  window.dataLayer.push({
    event: "consent_update",
    ad_consent: value,
    gpc: hasGlobalPrivacyControl(),
  });
  // Log the choice for the admin dashboard's consent stats.
  void import("./siteEvents").then(({ logSiteEvent }) =>
    logSiteEvent("consent_update", { ad_consent: value, gpc: hasGlobalPrivacyControl() }),
  );
};

/** Ask the cookie preferences UI to open (used by the footer link). */
export const openCookiePreferences = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT));
};
